import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type {
  ApiResponse,
  CompanyWarranty,
  CompanyWarrantyUpsertPayload,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Sub-recurso de garantía de empresa.
 *
 * Endpoints:
 *   - GET `/api/structure/companies/{companyId}/warranties`
 *   - PUT `/api/structure/companies/{companyId}/warranties` (multipart/form-data)
 *
 * El PUT espera claves repetidas `files` + `orders` en paralelo.
 * El GET puede devolver `data` vacío u omitir campos si aún no hay garantía.
 */
@Injectable({ providedIn: 'root' })
export class CompanyWarrantyService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  get(companyId: string): Observable<CompanyWarranty | null> {
    return this.httpClient
      .get<ApiResponse<CompanyWarranty | null>>(
        `${this.apiUrl}/${companyId}/warranties`,
      )
      .pipe(map((response) => response.data ?? null));
  }

  update(
    companyId: string,
    payload: CompanyWarrantyUpsertPayload,
  ): Observable<CompanyWarranty | null> {
    return this.httpClient
      .put<ApiResponse<CompanyWarranty | null>>(
        `${this.apiUrl}/${companyId}/warranties`,
        this.toFormData(payload),
      )
      .pipe(map((response) => response.data ?? null));
  }

  /**
   * Multipart alineado al contrato Postman:
   *   mandatory, typeWarrantyId, coinId, amount, observations,
   *   files (File, repetido), orders (text, repetido en paralelo).
   */
  private toFormData(payload: CompanyWarrantyUpsertPayload): FormData {
    const formData = new FormData();

    formData.append('mandatory', String(payload.mandatory));

    if (payload.typeWarrantyId) {
      formData.append('typeWarrantyId', payload.typeWarrantyId);
    }
    if (payload.coinId) {
      formData.append('coinId', payload.coinId);
    }
    if (payload.amount != null && payload.amount !== '') {
      formData.append('amount', String(payload.amount));
    }
    if (payload.observations != null && payload.observations !== '') {
      formData.append('observations', payload.observations);
    }

    const files: File[] = payload.files ?? [];
    const orders: number[] = payload.orders ?? [];

    files.forEach((file: File, index: number) => {
      formData.append('files', file);
      const order = orders[index] ?? index + 1;
      formData.append('orders', String(order));
    });

    return formData;
  }
}
